import type { ReactNode } from 'react';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import type { IRequest } from '../../../../common/interfaces/IRequest';
import type { IAuthenticatedUser } from '../../../../common/utils/IAuthenticatedUser';
import type {
    IDermaOthProcedureAddValidated,
    IDermaOthProcedureCustomControlData,
} from '../../../../common/validation/customControls/dermatology';
import type { IReduxState } from '../../../configureStore';
import type { DispatchType } from '../../../store/utils/dispatchType';
import type { ValidationErrors } from '../../../utils/validation';
import { ServiceCode } from '../../../../common/enums/ServiceCode';
import {
    dermaOthProcedureSchema,
    step2RegularSchema,
    step3RegularSchema,
    step4RegularSchema,
} from '../../../../common/validation/customControls/dermatology';
import { receiveUserData } from '../../../store/authentication/actions';
import { fetchJson } from '../../../store/fetch';
import { isPendingRequestForService } from '../../../utils/isPendingRequestForService';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { BackButton } from '../../elements/atoms/backButton/BackButton';
import { Main } from '../../elements/molecules/main/Main';
import { ProgressBar } from '../../elements/molecules/progressBar/ProgressBar';
import { Footer } from '../../elements/organisms/footer/Footer';
import { Header } from '../../elements/organisms/header/Header';
import { PageWrapper } from '../../elements/organisms/pageWrapper/PageWrapper';
import { Intro } from './steps/common/Intro';
import { Step1 } from './steps/common/Step1';
import { Step2 } from './steps/common/Step2';
import { Step3 } from './steps/common/Step3';
import { Step4 } from './steps/common/Step4';
import { Step5 } from './steps/common/Step5';
import { ProcedureSentMessage } from './steps/regular/ProcedureSent';

export interface IRegularStepProps {
    errors: ValidationErrors;
    handleChange: (update: Partial<IDermaOthProcedureCustomControlData>) => void;
    isSubmitting?: boolean;
    onUploadStatusChanged?: (isUploading: boolean) => void;
    procedure: IDermaOthProcedureAddValidated;
    submit?: () => void;
    validateAndGoToNextStep: () => void;
}

interface INewProcedureReduxButtonProps {
    canCreateDermaOthProcedure: boolean;
    processes?: IRequest[];
}

const mapStateToProps = ({ authentication }: IReduxState): INewProcedureReduxButtonProps => ({
    canCreateDermaOthProcedure: authentication.permissions?.canCreateDermaOthProcedure || false,
    processes: authentication.requestProcesses,
});

type NewProcedureProps = INewProcedureReduxButtonProps & ReturnType<typeof mapDispatchToProps>;

function NewDermaOthProcedureWrapper(props: NewProcedureProps) {
    const navigate = useNavigate();
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [procedure, setProcedure] = useState<IDermaOthProcedureAddValidated>({
        customControlData: {
            condition: {
                anders: null,
                jeukt: false,
                pijn: false,
            },
            conditionBefore: null,
            conditionBeforeRemark: null,
            conditionChanged: null,
            conditionColorChanged: null,
            conditionSizeChanged: null,
            conditionStarted: null,
            detailFiles: [],
            extraQuestion: null,
            overviewFiles: [],
            treatingCondition: null,
            treatingConditionRemark: null,
        },
    });

    const [currentStep, setCurrentStep] = useState<number>(0);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [areValidationsActivated, setAreValidationsActivated] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        const pendingDermaOthProcedure = props.processes?.find((request) =>
            isPendingRequestForService(request, ServiceCode.DermaOth)
        );
        if (props.processes && !pendingDermaOthProcedure) {
            navigate('/');
        }

        if (pendingDermaOthProcedure) {
            const procedureId = pendingDermaOthProcedure.procedure?.id;
            setProcedure({
                ...procedure,
                procedureId,
            });
        }
    }, [props.processes]);

    useEffect(() => {
        if (areValidationsActivated) {
            validateOnChange();
        }
    }, [procedure]);

    const getValidationSchemaByStep = (step: number) => {
        switch (step) {
            case 0:
                return null;
            case 1:
                return null;
            case 2:
                return step2RegularSchema;
            case 3:
                return step3RegularSchema;
            case 4:
                return step4RegularSchema;
            case 5:
                return null;
            default:
                throw new Error('Schema not defined for step ' + step);
        }
    };

    const validateAndGoToNextStep = () => {
        setAreValidationsActivated(true);
        const schema = getValidationSchemaByStep(currentStep);
        if (schema) {
            const validationResult = validate(schema, procedure.customControlData);
            if (validationResult.errors.size === 0) {
                goToNextStep();
            } else {
                setErrors(validationResult.errors);
            }
        } else {
            goToNextStep();
        }
    };

    const validateOnChange = () => {
        const schema = getValidationSchemaByStep(currentStep);
        if (schema) {
            const validationResult = validate(schema, procedure.customControlData);
            setErrors(validationResult.errors);
        }
    };

    const goToPrevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const goToNextStep = () => {
        setAreValidationsActivated(false);
        setErrors(emptyValidationErrors);
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleChange = (update: Partial<IDermaOthProcedureCustomControlData>) => {
        setProcedure({ ...procedure, customControlData: { ...procedure.customControlData, ...update } });
    };

    const submitProcedure = async () => {
        setIsSubmitting(true);
        const validationResult = validate(dermaOthProcedureSchema, procedure);
        if (validationResult.errors.size === 0) {
            const result = await fetchJson(
                `/api/patient/homeExamination?serviceCode=${ServiceCode.DermaOth}`,
                'POST',
                getPostData()
            );
            goToNextStep();
            if (result.json && result.json.patientData) {
                props.receiveUserData(result.json.patientData);
            }
        } else {
            setIsSubmitting(false);
        }
    };

    const getPostData = () => {
        const result = JSON.parse(JSON.stringify(procedure));

        delete result.customControlData.overviewFiles[0].thumbnailDataUrl;
        delete result.customControlData.overviewFiles[0].placement;
        for (const detailFile of result.customControlData.detailFiles) {
            delete detailFile.thumbnailDataUrl;
            delete detailFile.placement;
        }

        return { procedure: result };
    };

    const stepProps = {
        errors,
        handleChange,
        procedure,
        validateAndGoToNextStep,
    };

    const steps: ReactNode[] = [
        <Intro
            key={0}
            {...stepProps}
        />,
        <Step1
            key={1}
            {...stepProps}
        />,
        <Step2
            key={2}
            {...stepProps}
            onUploadStatusChanged={(uploadingStatus) => setIsUploading(uploadingStatus)}
        />,
        <Step3
            key={3}
            {...stepProps}
            onUploadStatusChanged={(uploadingStatus) => setIsUploading(uploadingStatus)}
        />,
        <Step4
            key={4}
            {...stepProps}
        />,
        <Step5
            key={5}
            {...stepProps}
            isSubmitting={isSubmitting}
            submit={submitProcedure}
        />,
        <ProcedureSentMessage key={6} />,
    ];

    if (props.canCreateDermaOthProcedure) {
        const backButton = (
            <BackButton
                disabled={isUploading}
                goBackFunction={goToPrevStep}
            />
        );

        return (
            <PageWrapper
                fullHeight
                scrollToTopUpdater={currentStep}
            >
                <Header
                    customBackButton={backButton}
                    hasBackbutton={currentStep > 0 && currentStep < steps.length - 1 && !isSubmitting}
                />
                {currentStep > 0 && currentStep < steps.length - 1 && (
                    <ProgressBar progress={(currentStep / (steps.length - 1)) * 100}></ProgressBar>
                )}
                <Main>{currentStep >= 0 && currentStep < steps.length && steps[currentStep]}</Main>
                <Footer />
            </PageWrapper>
        );
    } else {
        return null;
    }
}

function mapDispatchToProps(dispatch: DispatchType) {
    return {
        dispatch,
        receiveUserData: (userData: Partial<IAuthenticatedUser>) => {
            dispatch(receiveUserData(userData));
        },
    };
}

export const NewDermaOthProcedure = connect(mapStateToProps, mapDispatchToProps)(NewDermaOthProcedureWrapper);
